import { useState, type ReactElement } from 'react';
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import type { ClientFmvTimeseriesPoint } from '../dto/clientDashboard.js';
import type {
  TelemetryScoreHistoryClientItem,
  TelemetryScoreHistoryOpsItem,
} from '../dto/telemetry-score.js';
import { SX_PALETTE } from './charts.js';
import { cn } from './utils.js';
import { formatDate, formatTs, formatUsd } from './format.js';

// Theme-token colors with hardcoded fallbacks so the same chart renders
// correctly in the ops portal (defines the CSS vars) and the customer
// portal (doesn't).
const BORDER = 'hsl(var(--border, 240 5% 91%))';
const MUTED = 'hsl(var(--muted-foreground, 215 20% 65%))';
const TOOLTIP_STYLE = {
  fontSize: 12,
  borderRadius: 6,
  borderColor: BORDER,
  backgroundColor: 'hsl(var(--popover, 0 0% 100%))',
  color: 'hsl(var(--popover-foreground, 240 10% 4%))',
} as const;

function FmvLineBase({
  data,
  xKey,
  xFormat,
  lines,
  height,
  emptyText,
  yDomain,
  yTickFormat = (v) => formatUsd(v, { compact: true }),
  tooltipValueFormat = (v) => formatUsd(v),
}: {
  data: unknown[];
  xKey: string;
  xFormat: (v: string) => string;
  lines: {
    key: string;
    name: string;
    primary?: boolean;
    // Per-point custom dot renderer (SIL-518 marker dots). Falls back to the
    // default { r: 3 } / hidden dot when omitted. recharts calls this once
    // per data point with a plain props object (not JSX) — `key` must be
    // read off it and applied explicitly, recharts does not clone/key the
    // returned element for us.
    dot?: (props: {
      key?: string;
      cx?: number;
      cy?: number;
      payload?: Record<string, unknown>;
    }) => ReactElement;
  }[];
  height: number;
  emptyText: string;
  yDomain?: [number, number];
  yTickFormat?: (v: number) => string;
  tooltipValueFormat?: (v: number) => string;
}) {
  if (data.length === 0) {
    return (
      <div className="flex h-48 items-center justify-center text-xs text-muted-foreground">
        {emptyText}
      </div>
    );
  }

  return (
    <ResponsiveContainer width="100%" height={height}>
      <LineChart data={data} margin={{ top: 10, right: 16, bottom: 4, left: 8 }}>
        <CartesianGrid strokeDasharray="3 3" stroke={BORDER} />
        <XAxis dataKey={xKey} tickFormatter={xFormat} stroke={MUTED} fontSize={11} tickLine={false} />
        <YAxis
          stroke={MUTED}
          fontSize={11}
          tickLine={false}
          domain={yDomain}
          tickFormatter={(v) => yTickFormat(v as number)}
        />
        <Tooltip
          formatter={(value: number) => tooltipValueFormat(value)}
          labelFormatter={(label: string) => xFormat(label)}
          contentStyle={TOOLTIP_STYLE}
        />
        {lines.map((l, i) => (
          <Line
            key={l.key}
            type="monotone"
            dataKey={l.key}
            stroke={SX_PALETTE[l.primary ? 0 : i + 1]}
            strokeWidth={l.primary ? 2 : 1.5}
            dot={l.dot ?? (l.primary ? { r: 3 } : false)}
            name={l.name}
          />
        ))}
      </LineChart>
    </ResponsiveContainer>
  );
}

/**
 * Minimal point shape FmvHistoryLine actually reads. Structurally satisfied
 * by both the ops-audience `AssetSnapshotHistoryItem` (fmvLowUsd/fmvHighUsd
 * present, used when `bands` is set) and the narrower customer-portal
 * `ClientFmvSnapshot` (apps/customer/src/components/asset-detail/
 * asset-detail-types.ts — the customer role is never granted fmvLowUsd/
 * fmvHighUsd, per the 578/582 doctrine), so callers on either side pass
 * their own real DTO without a cast.
 */
export interface FmvHistoryLinePoint {
  pricedAt: string;
  fmvMedUsd: number;
  fmvLowUsd?: number;
  fmvHighUsd?: number;
}

/**
 * FMV time-series for an asset's snapshot history. Median-only by default
 * (customer portal — low/high bands are ops-internal); pass `bands` for the
 * three-line ops view.
 */
export function FmvHistoryLine({
  data,
  bands = false,
}: {
  data: FmvHistoryLinePoint[];
  bands?: boolean;
}) {
  // Recharts wants oldest-first for left-to-right rendering.
  const series = [...data].sort(
    (a, b) => new Date(a.pricedAt).getTime() - new Date(b.pricedAt).getTime(),
  );
  const lines = bands
    ? [
        { key: 'fmvLowUsd', name: 'Low' },
        { key: 'fmvMedUsd', name: 'Median', primary: true },
        { key: 'fmvHighUsd', name: 'High' },
      ]
    : [{ key: 'fmvMedUsd', name: 'FMV Median', primary: true }];
  return (
    <FmvLineBase
      data={series}
      xKey="pricedAt"
      xFormat={formatTs}
      lines={lines}
      height={220}
      emptyText="No snapshots yet."
    />
  );
}

// SIL-518: on a day where one or more assets had their first-ever FMV
// snapshot, the plotted total jumps by their full value (no prior history to
// diff against) — a data-history boundary, not a real value event. Mark it
// with a small amber dot; the line/values themselves are never altered. Hover
// (native SVG <title>) is the only affordance — no dashed guide, no
// always-visible chip, matching the validated prototype.
function newlyPricedDot(props: {
  key?: string;
  cx?: number;
  cy?: number;
  payload?: Record<string, unknown>;
}) {
  const { key, cx, cy, payload } = props;
  const count = Number(payload?.newlyPricedAssetCount ?? 0);
  if (!cx || !cy || count <= 0) {
    // Same default dot as every other primary-line point.
    return <circle key={key} cx={cx} cy={cy} r={3} fill={SX_PALETTE[0]} stroke="none" />;
  }
  const label =
    count === 1
      ? '1 asset newly priced, no prior FMV history'
      : `${count} assets newly priced, no prior FMV history`;
  return (
    <circle
      key={key}
      cx={cx}
      cy={cy}
      r={5}
      fill={SX_PALETTE[4]}
      stroke="hsl(var(--popover, 0 0% 100%))"
      strokeWidth={1.5}
    >
      <title>{label}</title>
    </circle>
  );
}

// SIL-523: 7-day trailing rolling average of totalFmvMedUsd, computed
// client-side from the already-fetched series (oldest-first). Windows near
// the start of the series average over however many days are available (no
// padding/backfill) rather than being left undefined.
const SMOOTHING_WINDOW_DAYS = 7;

function trailingRollingAverage(
  series: ClientFmvTimeseriesPoint[],
): (ClientFmvTimeseriesPoint & { totalFmvMedUsd: number })[] {
  return series.map((point, i) => {
    const windowStart = Math.max(0, i - SMOOTHING_WINDOW_DAYS + 1);
    const window = series.slice(windowStart, i + 1);
    const avg = window.reduce((sum, p) => sum + p.totalFmvMedUsd, 0) / window.length;
    return { ...point, totalFmvMedUsd: avg };
  });
}

type FmvOverTimeMode = 'raw' | 'smoothed';

function FmvOverTimeModeToggle({
  mode,
  onChange,
}: {
  mode: FmvOverTimeMode;
  onChange: (mode: FmvOverTimeMode) => void;
}) {
  const options: { value: FmvOverTimeMode; label: string }[] = [
    { value: 'raw', label: 'Raw' },
    { value: 'smoothed', label: 'Smoothed' },
  ];
  return (
    <div
      role="group"
      aria-label="Portfolio FMV over time — raw or smoothed"
      className="inline-flex rounded-md border border-border p-0.5 text-xs"
    >
      {options.map((opt) => (
        <button
          key={opt.value}
          type="button"
          aria-pressed={mode === opt.value}
          onClick={() => onChange(opt.value)}
          className={cn(
            'rounded px-2 py-1 font-medium transition-colors',
            mode === opt.value
              ? 'bg-primary text-primary-foreground'
              : 'text-muted-foreground hover:bg-muted hover:text-foreground',
          )}
        >
          {opt.label}
        </button>
      ))}
    </div>
  );
}

/**
 * Portfolio FMV-over-time line for the seller dashboard: daily granularity,
 * whole-portfolio median total per day.
 *
 * SIL-523: Raw/Smoothed toggle. Raw (default) is the true unaltered daily
 * total, unchanged from SIL-518. Smoothed plots a 7-day trailing rolling
 * average of the same data instead — computed client-side, no API change.
 * Gap markers (newlyPricedDot) stay visible in both views since the
 * underlying "no prior FMV history" event is real regardless of how the
 * line is drawn.
 */
export function FmvOverTimeLine({ data }: { data: ClientFmvTimeseriesPoint[] }) {
  const [mode, setMode] = useState<FmvOverTimeMode>('raw');
  const series = [...data].sort(
    (a, b) => new Date(a.day).getTime() - new Date(b.day).getTime(),
  );
  const plotted = mode === 'smoothed' ? trailingRollingAverage(series) : series;
  return (
    <div>
      {data.length > 0 && (
        <div className="mb-2 flex justify-end">
          <FmvOverTimeModeToggle mode={mode} onChange={setMode} />
        </div>
      )}
      <FmvLineBase
        data={plotted}
        xKey="day"
        xFormat={(v) => formatDate(v)}
        lines={[
          {
            key: 'totalFmvMedUsd',
            name: mode === 'smoothed' ? 'Portfolio FMV (7d avg)' : 'Portfolio FMV (med)',
            primary: true,
            dot: newlyPricedDot,
          },
        ]}
        height={240}
        emptyText="No pricing history yet."
      />
    </div>
  );
}

/**
 * Telemetry health-score trend for an asset's score-run history (RUF-518).
 * Overall score always plots; pass `categories` (a fixed set of category
 * keys, e.g. from the latest run) to overlay per-category lines too — same
 * shape whether the data came from the ops or client history endpoint since
 * both carry `overallScore` + `categories[].{category,score}`.
 */
export function TelemetryScoreHistoryLine({
  data,
  categories = [],
}: {
  data: (TelemetryScoreHistoryOpsItem | TelemetryScoreHistoryClientItem)[];
  categories?: string[];
}) {
  const series = [...data]
    .sort((a, b) => new Date(a.scoredAt).getTime() - new Date(b.scoredAt).getTime())
    .map((item) => {
      const row: Record<string, string | number> = {
        scoredAt: item.scoredAt,
        overallScore: item.overallScore,
      };
      for (const cat of item.categories) {
        row[`cat_${cat.category}`] = cat.score;
      }
      return row;
    });

  const lines = [
    { key: 'overallScore', name: 'Overall', primary: true },
    ...categories.map((category) => ({
      key: `cat_${category}`,
      name: category.replace(/_/g, ' '),
    })),
  ];

  return (
    <FmvLineBase
      data={series}
      xKey="scoredAt"
      xFormat={formatTs}
      lines={lines}
      height={220}
      emptyText="No score history yet."
      yDomain={[0, 100]}
      yTickFormat={(v) => v.toFixed(0)}
      tooltipValueFormat={(v) => v.toFixed(1)}
    />
  );
}
